import { Button, Grid, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationHeader from '../ConfirmationHeader/ConfirmationHeader';

import { useAppSelector } from '../../../app/hooks/hooks';
import StepProgressBar from '../StepProgressBar/StepProgressBar';

import { useSelector } from 'react-redux';
import { getClaimCreationProductType, getPartnerId } from '../../../app/store/combinedSelectors';
import { goToClaimsBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { GenericProductType, IconStateType, PartnerId } from '../../../types/type';
import { postMessageToParentApp } from '../../../utils/Utilities';
import ModalLinearActivityIndicator from '../ModalLinearActivityIndicator/ModalLinearActivityIndicator';

export default function TimeoutConfirmation() {
  const theme: Theme = useTheme();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const headerContent = 'Thank you for your claim submission';
  const navigate = useNavigate();

  const staticProgressStages = useAppSelector((state) => state.services.staticProgressStages);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);

  const typeOfClaim = useSelector(getClaimCreationProductType);
  const partnerId = useSelector(getPartnerId);
  const isProperty = typeOfClaim === GenericProductType.Home;

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    }
  }

  const introContent = (
    <Grid container>
      <Grid item xs={12}>
        <Typography sx={{ marginTop: '20px' }} variant="body1">
          You should receive a confirmation email for your records.
        </Typography>

        {isProperty && (
          <Typography sx={{ marginTop: '1em' }} variant="body1">
            We cannot confirm or deny coverage for your claim at this time.
          </Typography>
        )}
        <Typography sx={{ marginTop: '1em' }} variant="body1">
          If you don't receive a confirmation email in the next 24 hours, please resubmit your claim.
        </Typography>
        <Typography sx={{ marginTop: '1em' }} variant="body1">
          Tap <b>Go to Claims</b> to monitor your claim and keep your claim moving forward.
        </Typography>
      </Grid>
    </Grid>
  );

  /* If there are eligible services, the total number of stages will be more than 2 (Report and Complete are in the stages collection by default) */
  const content = (
    <>
      <StepProgressBar stateInfo={staticProgressStages}></StepProgressBar>

      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader headerContent={headerContent} state={IconStateType.WARNING_STATE}></ConfirmationHeader>
        </Grid>

        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.serviceConfirmationHeader,
              marginTop: '1rem',
            }}
          >
            Next Steps
          </Typography>
        </Grid>
        {introContent}
      </Grid>

      <Grid
        container
        sx={{
          marginTop: '3em',
          marginLeft: '0 auto',
          marginRight: '0 auto',
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
        }}
      >
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Button
              sx={{
                ...theme.custom.styles.ctaNextButton,
                '&.MuiButtonBase-root:hover': {
                  bgcolor: theme.custom.colors.primaryButtonHoverColor,
                },
              }}
              onClick={goToClaimsOverview}
            >
              {goToClaimsBtn}
            </Button>
          </Grid>
        </Grid>
        {modalLoadingOpen && <ModalLinearActivityIndicator />}
      </Grid>
    </>
  );
  return <Box sx={{ width: '100%' }}>{content}</Box>;
}
